import { LINKS } from "@silo/types";
import { DescriptionExternalLink } from "../../description";
import { KeyValueList } from "../../key-value-list";
import { DebtMetrics } from "./types";
import { BreakdownDebt } from "../../apr-display-v2/breakdown/breakdown-debt";
import { match, P } from "ts-pattern";
import { FutureProjections } from "./future-projections";
import { TotalApr } from "./total-apr";
import { cn } from "../../../utils/class-names";
import { getUtilizationClassName } from "../utils";

interface ProjectedAprProps {
  currentMetrics: DebtMetrics;
  newMetrics: DebtMetrics | null;
}

export function ProjectedApr({
  currentMetrics,
  newMetrics,
}: ProjectedAprProps): JSX.Element {
  const metrics = newMetrics ?? currentMetrics;

  return (
    <KeyValueList.Pair
      description={
        <div className="flex flex-col gap-2.5">
          <p>
            An estimated Annual Percentage Rate that you will likely pay after
            completing the transaction. The value is calculated based on a
            projected utilization of the token asset in question.
          </p>

          <DescriptionExternalLink href={LINKS.SILOPEDIA.PROJECTED_APY} />
        </div>
      }
      trailingContent={
        newMetrics ? <TrailingContent newMetrics={newMetrics} /> : undefined
      }
      trailingContentContainerClassName="gap-0"
    >
      <KeyValueList.Key>Projected APR</KeyValueList.Key>

      <KeyValueList.Value>
        {match({ currentMetrics, newMetrics })
          .with({ newMetrics: P.nonNullable }, ({ newMetrics }) => (
            <TotalApr
              metrics={newMetrics}
              className={cn(
                newMetrics.hasDebtRewards ? "text-2xl" : "text-base",
                getUtilizationClassName(newMetrics.utilizationState)
              )}
            />
          ))
          .with(
            {
              currentMetrics: {
                projectionsState: {
                  _tag: P.union("increasing", "decreasing"),
                },
              },
            },
            ({
              currentMetrics: {
                totalDebtRewardsApr,
                projectionsState: { baseApr1d, baseApr3d },
                utilizationState,
              },
            }) => (
              <FutureProjections
                totalDebtRewardsApr={totalDebtRewardsApr}
                debtBaseApr1d={baseApr1d}
                debtBaseApr3d={baseApr3d}
                utilizationState={utilizationState}
              />
            )
          )
          .otherwise(() => "--")}
      </KeyValueList.Value>
    </KeyValueList.Pair>
  );
}

interface TrailingContentProps {
  newMetrics: DebtMetrics;
}

function TrailingContent({
  newMetrics,
}: TrailingContentProps): JSX.Element | null {
  const {
    hasDebtRewards,
    projectionsState,
    debtRewards,
    debtBaseApr,
    totalDebtRewardsApr,
    utilizationState,
    isDebtAprFixed,
  } = newMetrics;

  const hasTrailingContent =
    hasDebtRewards || projectionsState._tag !== "stable";

  if (!hasTrailingContent) return null;

  return (
    <div className="flex flex-col gap-5">
      {hasDebtRewards && (
        <BreakdownDebt
          className="gap-0 text-sm"
          debtRewards={debtRewards}
          debtBaseApr={debtBaseApr}
          isDebtAprFixed={isDebtAprFixed}
          align="right"
        />
      )}

      {match(projectionsState)
        .with(
          { _tag: P.union("increasing", "decreasing") },
          ({ baseApr1d, baseApr3d }) => (
            <FutureProjections
              totalDebtRewardsApr={totalDebtRewardsApr}
              debtBaseApr1d={baseApr1d}
              debtBaseApr3d={baseApr3d}
              utilizationState={utilizationState}
            />
          )
        )
        .otherwise(() => null)}
    </div>
  );
}
